import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
  OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatChipSelectionChange,
  MatChipsModule,
} from '@angular/material/chips';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

interface FilterOption {
  id: string;
  label: string;
  selected?: boolean;
}

@Component({
  selector: 'lib-chip-filter',
  imports: [
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatCheckboxModule,
    MatDivider,
  ],
  template: `
    <div class="filter-tags-container">
      <mat-chip-listbox
        multiple
        aria-label="Filter options"
        class="filter-chips"
      >
        @for (option of options(); track option.id) {
          <mat-chip-option
            [id]="option.id"
            [selected]="isSelected(option.id)"
            [class.custom-selected]="isSelected(option.id)"
            (selectionChange)="onSelectionChange($event)"
          >
            <span class="mat-label-medium">
              {{ option.label }}
            </span>
          </mat-chip-option>
        }
      </mat-chip-listbox>

      <button
        class="filter-button"
        mat-fab
        [matMenuTriggerFor]="menu"
        aria-label="More filter options"
      >
        <mat-icon>filter_list</mat-icon>
      </button>

      <mat-menu #menu="matMenu">
        <mat-checkbox
          class="select-all-checkbox"
          [checked]="isAllSelected()"
          [indeterminate]="isPartiallySelected()"
          (change)="toggleSelectAll()"
        >
          Select All
        </mat-checkbox>
        <mat-divider></mat-divider>
        @for (option of options(); track option.id) {
          <mat-checkbox
            [checked]="isSelected(option.id)"
            (change)="toggleOption(option.id, $event.checked)"
          >
            {{ option.label }}
          </mat-checkbox>
        }
      </mat-menu>
    </div>
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;

    :host {
      display: block;
    }

    .filter-tags-container {
      display: flex;
      align-items: center;
    }

    .filter-chips {
      display: flex;
      flex-wrap: wrap;
      flex: 1;
      gap: 8px;
    }

    .custom-selected {
      background-color: mat.get-theme-color(
        theme.$light-theme,
        secondary-container
      );
      color: mat.get-theme-color(theme.$light-theme, on-secondary-container);
    }

    .filter-button {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      bakcground-color: mat.get-theme-color(
        theme.$light-theme,
        primary-container
      );
      color: mat.get-theme-color(theme.$light-theme, primary);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipFilterComponent implements OnInit {
  readonly options = input.required<FilterOption[]>();
  readonly selectedOptions = model<string[]>([]);

  ngOnInit(): void {
    const initialSelected = this.options()
      .filter((option) => option.selected)
      .map((option) => option.id);

    if (initialSelected.length > 0) {
      this.selectedOptions.set(initialSelected);
    }
  }

  protected isSelected(optionId: string): boolean {
    return this.selectedOptions().includes(optionId);
  }

  protected isPartiallySelected(): boolean {
    const selectedCount = this.selectedOptions().length;
    return selectedCount > 0 && selectedCount < this.options().length;
  }

  protected isAllSelected(): boolean {
    return this.selectedOptions().length === this.options().length;
  }

  protected onSelectionChange(event: MatChipSelectionChange): void {
    const chipId = event.source.id;
    const selected = event.selected;
    this.toggleOption(chipId, selected);
  }

  protected toggleOption(optionId: string, selected: boolean): void {
    const currentSelection = this.selectedOptions();
    let newSelection: string[];

    if (selected) {
      if (!currentSelection.includes(optionId)) {
        newSelection = [...currentSelection, optionId];
      } else {
        newSelection = currentSelection;
      }
    } else {
      newSelection = currentSelection.filter((id) => id !== optionId);
    }

    this.selectedOptions.set(newSelection);
  }

  protected toggleSelectAll(): void {
    const allSelected = this.isAllSelected();
    const newSelection = allSelected
      ? []
      : this.options().map((option) => option.id);
    this.selectedOptions.set(newSelection);
  }
}
